import axiosInstance from './AxiosInstance';

/**
 * Vehicle Management Service
 * All API calls for vehicle management including CRUD operations
 * for vehicles, documents, insurance, and registration
 */

// ==================== VEHICLE CRUD ====================

/**
 * Create a new vehicle
 * @param {Object} vehicleData - Vehicle data
 * @returns {Promise}
 */
export const createVehicle = (vehicleData) => {
  return axiosInstance.post('/vehicle/addVehicle', vehicleData);
};

/**
 * Update vehicle
 * @param {number} vehicleId - Vehicle ID
 * @param {Object} vehicleData - Vehicle data
 * @returns {Promise}
 */
export const updateVehicle = (vehicleId, vehicleData) => {
  return axiosInstance.put(`/vehicle/updateVehicle/${vehicleId}`, vehicleData);
};

/**
 * Get vehicle by ID
 * @param {number} vehicleId - Vehicle ID
 * @returns {Promise}
 */
export const getVehicleById = (vehicleId) => {
  return axiosInstance.get(`/vehicle/getVehicleById/${vehicleId}`);
};

/**
 * List vehicles by corporate with pagination and filters
 * @param {number} corporateId - Corporate ID
 * @param {Object} params - Query parameters (page, limit, search, status)
 * @returns {Promise}
 */
export const listVehiclesByCorporate = (corporateId, params = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('corporate_id', corporateId);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  
  return axiosInstance.get(`/vehicle/getVehicleList?${queryParams.toString()}`);
};

/**
 * Delete/Deactivate vehicle
 * @param {number} vehicleId - Vehicle ID
 * @param {number} updatedBy - User ID who is performing the action
 * @returns {Promise}
 */
export const deactivateVehicle = (vehicleId, updatedBy) => {
  return axiosInstance.put(`/vehicle/deactivateVehicle`, { 
    vehicle_id: vehicleId, 
    updated_by: updatedBy 
  });
};

// ==================== VEHICLE DOCUMENTS ====================

/**
 * Create vehicle document
 * @param {number} vehicleId - Vehicle ID
 * @param {FormData|Object} documentData - Document data (FormData for file upload or Object for JSON)
 * @returns {Promise}
 */
export const createVehicleDocument = (vehicleId, documentData) => {
  const isFormData = documentData instanceof FormData;
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  } : {};
  return axiosInstance.post(`/vehicle/${vehicleId}/documents`, documentData, config);
};

/**
 * Update vehicle document
 * @param {number} vehicleId - Vehicle ID
 * @param {number} documentId - Document ID
 * @param {FormData|Object} documentData - Document data (FormData for file upload or Object for JSON)
 * @returns {Promise}
 */
export const updateVehicleDocument = (vehicleId, documentId, documentData) => {
  const isFormData = documentData instanceof FormData;
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  } : {};
  return axiosInstance.put(`/vehicle/${vehicleId}/documents/${documentId}`, documentData, config);
};

/**
 * Delete vehicle document
 * @param {number} vehicleId - Vehicle ID
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const deleteVehicleDocument = (vehicleId, documentId) => {
  return axiosInstance.delete(`/vehicle/${vehicleId}/documents/${documentId}`);
};

/**
 * Get vehicle documents
 * @param {number} vehicleId - Vehicle ID
 * @returns {Promise}
 */
export const getVehicleDocuments = (vehicleId) => {
  return axiosInstance.get(`/vehicle/${vehicleId}/documents`);
};

// ==================== VEHICLE INSURANCE ====================

/**
 * Create vehicle insurance
 * @param {number} vehicleId - Vehicle ID
 * @param {Object} insuranceData - Insurance data
 * @returns {Promise}
 */
export const createVehicleInsurance = (vehicleId, insuranceData) => {
  return axiosInstance.post(`/vehicle/${vehicleId}/insurances`, insuranceData);
};

/**
 * Update vehicle insurance
 * @param {number} vehicleId - Vehicle ID
 * @param {number} insuranceId - Insurance ID
 * @param {Object} insuranceData - Insurance data
 * @returns {Promise}
 */
export const updateVehicleInsurance = (vehicleId, insuranceId, insuranceData) => {
  return axiosInstance.put(`/${vehicleId}/insurances/${insuranceId}`, insuranceData);
};

/**
 * Delete vehicle insurance
 * @param {number} vehicleId - Vehicle ID
 * @param {number} insuranceId - Insurance ID
 * @returns {Promise}
 */
export const deleteVehicleInsurance = (vehicleId, insuranceId) => {
  return axiosInstance.delete(`/vehicle/${vehicleId}/insurances/${insuranceId}`);
};

/**
 * Get vehicle insurances
 * @param {number} vehicleId - Vehicle ID
 * @returns {Promise}
 */
export const getVehicleInsurances = (vehicleId) => {
  return axiosInstance.get(`/vehicle/${vehicleId}/insurances`);
};

// ==================== VEHICLE REGISTRATION ====================

/**
 * Create vehicle registration
 * @param {number} vehicleId - Vehicle ID
 * @param {Object} registrationData - Registration data
 * @returns {Promise}
 */
export const createVehicleRegistration = (vehicleId, registrationData) => {
  return axiosInstance.post(`/vehicle/${vehicleId}/registrations`, registrationData);
};

/**
 * Update vehicle registration
 * @param {number} vehicleId - Vehicle ID
 * @param {number} registrationId - Registration ID
 * @param {Object} registrationData - Registration data
 * @returns {Promise}
 */
export const updateVehicleRegistration = (vehicleId, registrationId, registrationData) => {
  return axiosInstance.put(`/vehicle/${vehicleId}/registrations/${registrationId}`, registrationData);
};

/**
 * Delete vehicle registration
 * @param {number} vehicleId - Vehicle ID
 * @param {number} registrationId - Registration ID
 * @returns {Promise}
 */
export const deleteVehicleRegistration = (vehicleId, registrationId) => {
  return axiosInstance.delete(`/vehicle/${vehicleId}/registrations/${registrationId}`);
};

/**
 * Get vehicle registrations
 * @param {number} vehicleId - Vehicle ID
 * @returns {Promise}
 */
export const getVehicleRegistrations = (vehicleId) => {
  return axiosInstance.get(`/vehicle/${vehicleId}/registrations`);
};
